import { ORG_ADMIN, ORG_MEMBER, ORG_OWNER, Role } from "@xapp/stentor-api-client/dist/models/Roles";
import { isAdmin } from "auth";
import { StentorAdmin, STENTOR_ADMIN } from "auth/modules/roles";
import { UserProfile } from "auth/modules/state";
import { showNavItemForUser } from "components/SecondaryNav/components/utils";

/**
 * Who the drawer is for. It decides which sections render.
 *
 * - `super_admin`: a stentor admin. Admin section, any org, org items, businesses.
 * - `org`: someone with an org role (owner, admin or member). Org items gated by role, businesses.
 * - `business`: someone scoped to specific businesses. Only a card for each business.
 */
export type NavDrawerRole = "super_admin" | "org" | "business";

export interface NavDrawerOrganization {
    readonly organizationId: string;
    readonly name: string;
    /** Partner orgs get Partner Branding, same rule as the org bar (`type === "PARTNER"`). */
    readonly isPartner: boolean;
    readonly hasFleetManagement: boolean;
    readonly hasMapView: boolean;
}

export interface NavDrawerItem {
    readonly key: string;
    readonly label: string;
    readonly path: string;
    readonly showWithRoles?: (Role | StentorAdmin)[];
}

/** Roles that give access to the organization itself, as opposed to some of its apps. */
const ORG_LEVEL_ROLES: string[] = [ORG_OWNER, ORG_ADMIN, ORG_MEMBER];

/**
 * First path segments that are global pages rather than an organization id.
 */
const GLOBAL_ROUTES = new Set([
    "admin",
    "accept",
    "analytics",
    "apps",
    "graphql",
    "help",
    "leads",
    "list",
    "map",
    "organizations",
    "settings",
    "terms",
]);

/**
 * Fired on window to open the drawer from anywhere (the old org bar's "Show me").
 */
export const OPEN_NAV_DRAWER_EVENT = "studio:open-nav-drawer";

export function openNavDrawer(): void {
    window.dispatchEvent(new Event(OPEN_NAV_DRAWER_EVENT));
}

/**
 * Whether every org role the user holds is an app role - a get.xapp.ai dealer,
 * say, who reaches their own business and nothing of the org around it.
 */
export function isAppOnlyUser(profile: Pick<UserProfile, "roles"> | undefined): boolean {
    const held = (profile?.roles || []).filter((role) => role && (role.roles || []).length > 0);
    return held.length > 0 && held.every((role) => !(role.roles || []).some((r) => ORG_LEVEL_ROLES.includes(r)));
}

export function getOrgRoles(profile: Pick<UserProfile, "roles">, organizationId: string): string[] {
    const orgRoles = (profile?.roles || []).find((role) => role?.organizationId === organizationId);
    return (orgRoles?.roles || []).filter(Boolean);
}

/**
 * The organization the drawer is about: the one in the route, or on a global page,
 * the user's only organization.
 */
/** Whether the first path segment is a global page (e.g. /admin, /list) rather than an organization ID. */
export function isGlobalRouteSegment(segment: string | undefined): boolean {
    return !segment || GLOBAL_ROUTES.has(segment);
}

export function getCurrentOrganizationId(pathname: string, profile: Pick<UserProfile, "roles">): string | undefined {
    const segment = (pathname || "").split("/").filter(Boolean)[0];

    if (segment && !GLOBAL_ROUTES.has(segment)) {
        return segment;
    }

    const organizationIds = (profile?.roles || []).map((role) => role?.organizationId).filter(Boolean);

    return organizationIds.length === 1 ? organizationIds[0] : undefined;
}

export interface ResolveNavDrawerRoleArgs {
    readonly profile: UserProfile;
    readonly organizationId: string | undefined;
    /**
     * The `appIds` on the user's own policy for the organization, when known.
     * Null or empty means access to every business in the organization.
     */
    readonly restrictedAppIds?: readonly string[] | null;
}

export function resolveNavDrawerRole(args: ResolveNavDrawerRoleArgs): NavDrawerRole {
    const { profile, organizationId, restrictedAppIds } = args;

    if (isAdmin(profile)) {
        return "super_admin";
    }

    if (!organizationId) {
        return "org";
    }

    const orgRoles = getOrgRoles(profile, organizationId);

    if (orgRoles.includes(ORG_OWNER) || orgRoles.includes(ORG_ADMIN)) {
        return "org";
    }

    // App roles only (a get.xapp.ai dealer): they cannot read the org, so org items would all fail.
    if (orgRoles.length > 0 && !orgRoles.some((role) => ORG_LEVEL_ROLES.includes(role))) {
        return "business";
    }

    // An org member whose policy limits them to specific businesses.
    if (Array.isArray(restrictedAppIds) && restrictedAppIds.length > 0) {
        return "business";
    }

    return "org";
}

export interface GetOrgNavItemsArgs {
    readonly organization: NavDrawerOrganization;
    readonly profile: UserProfile;
    readonly asUser?: boolean;
}

/**
 * The org items for the drawer. Names and role gating match the old org bar
 * (OrganizationSecondaryNav); "Apps" becomes the BUSINESSES section.
 */
export function getOrgNavItems(args: GetOrgNavItemsArgs): NavDrawerItem[] {
    const { organization, profile, asUser } = args;
    const { organizationId, isPartner, hasFleetManagement, hasMapView } = organization;
    const base = `/${organizationId}`;

    const items: NavDrawerItem[] = [
        { key: "overview", label: "Overview", path: base },
        { key: "dashboard", label: "Leads Dashboard", path: `${base}/dashboard` },
        {
            key: "map",
            label: "Map",
            path: `${base}/map`,
            ...(hasMapView ? {} : { showWithRoles: [STENTOR_ADMIN] }),
        },
        { key: "leads", label: "Leads", path: `${base}/leads` },
    ];

    if (hasFleetManagement) {
        items.push({ key: "management", label: "Management", path: `${base}/management` });
    }

    items.push(
        { key: "analytics", label: "Analytics", path: `${base}/analytics`, showWithRoles: [STENTOR_ADMIN] },
        {
            key: "form-conversion",
            label: "Form Conversion",
            path: `${base}/forms/dashboard`,
            showWithRoles: [STENTOR_ADMIN],
        },
        { key: "members", label: "Members", path: `${base}/members`, showWithRoles: [ORG_ADMIN, ORG_OWNER] },
        { key: "settings", label: "Settings", path: `${base}/settings`, showWithRoles: [ORG_ADMIN, ORG_OWNER] }
    );

    if (isPartner) {
        items.push({
            key: "partner-branding",
            label: "Partner Branding",
            path: `${base}/settings/branding`,
            showWithRoles: [ORG_ADMIN, ORG_OWNER],
        });
    }

    return items.filter((item) => showNavItemForUser(item.showWithRoles, profile, organizationId, !!asUser));
}

/** Items only admins see (the old 🛠️ items), so the drawer can mark them. */
export function isAdminOnlyItem(item: Pick<NavDrawerItem, "showWithRoles">): boolean {
    return !!item.showWithRoles && item.showWithRoles.length === 1 && item.showWithRoles[0] === STENTOR_ADMIN;
}

/** Whether a nav path is where the user is: exact for roots, otherwise the path or anything under it. */
export function pathMatches(currentPath: string | undefined, path: string, exact = false): boolean {
    if (!currentPath) {
        return false;
    }
    const current = currentPath.replace(/\/+$/, "") || "/";
    return exact ? current === path : current === path || current.startsWith(`${path}/`);
}

/** Of several nav paths, the most specific one the user is on (so Settings isn't lit on Settings > Branding). */
export function mostSpecificMatch(currentPath: string | undefined, items: { key: string; path: string; exact?: boolean }[]): string | undefined {
    return items
        .filter((i) => pathMatches(currentPath, i.path, i.exact))
        .sort((a, b) => b.path.length - a.path.length)[0]?.key;
}
